import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
import { useState } from 'react';
import type { MessageDescriptor } from 'react-intl';
import {
  FormattedMessage,
  FormattedNumber,
  defineMessages,
  useIntl,
} from 'react-intl';
import styled from 'styled-components';

import {
  AveragePlayerScore,
  AverageStat,
} from '@sorare/core/src/__generated__/globalTypes';
import { FilterInDropdown } from '@sorare/core/src/atoms/dropdowns/FilterInDropdown';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { RadioGroup } from '@sorare/core/src/atoms/inputs/RadioGroup';
import { FullWidthContainer } from '@sorare/core/src/atoms/layout/FullWidthContainer';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { Tooltip } from '@sorare/core/src/atoms/tooltip/Tooltip';
import { BodyM, HeadlineS, LabelS } from '@sorare/core/src/atoms/typography';
import { PlayerScore } from '@sorare/core/src/components/scoring/PlayerScore';
import { Row } from '@sorare/core/src/components/seo/ui';
import { ANY_SPORT_PLAYERS_SLUG } from '@sorare/core/src/constants/__generated__/routes';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { FILTERS } from '@sorare/core/src/lib/filters';
import { cardAttributes, glossary } from '@sorare/core/src/lib/glossary';
import {
  attributes,
  getAppearancePercentage,
} from '@sorare/core/src/lib/players';
import { generateSportPath } from '@sorare/core/src/lib/routing/generateSportPath';

import { TopPlayer } from './TopPlayer/index.tsx';
import type {
  TopPlayersLeagueQuery,
  TopPlayersLeagueQueryVariables,
  TopPlayers_player,
} from './__generated__/index.graphql.ts';

const messages = defineMessages({
  cleanSheets: {
    id: 'League.Overview.TopPlayers.cleanSheets',
    defaultMessage: 'Clean Sheets',
  },
  bigChancesCreated: {
    id: 'League.Overview.TopPlayers.bigChancesCreated',
    defaultMessage: 'Big Chances Created',
  },
  tacklesWon: {
    id: 'League.Overview.TopPlayers.tacklesWon',
    defaultMessage: 'Tackles Won',
  },
  interceptionsWon: {
    id: 'League.Overview.TopPlayers.interceptionsWon',
    defaultMessage: 'Interceptions Won',
  },
});

const StyledLabelS = styled(LabelS)`
  text-transform: uppercase;
`;
const StyledHorizontal = styled(Horizontal)`
  justify-content: end;
`;
const Header = styled(LabelS)`
  text-transform: uppercase;
`;

type Props = {
  competitionSlug: string;
};

const playerFragment = gql`
  fragment TopPlayers_player on Player {
    slug
    displayName
    pictureUrl(derivative: "avatar")
    position
  }
` as TypedDocumentNode<TopPlayers_player>;

const TOP_PLAYERS_LEAGUE_QUERY = gql`
  query TopPlayersLeagueQuery(
    $slug: String!
    $averageStatForOrderedPlayers: AverageStat
    $averageStat: AverageStat!
    $queryScore: Boolean!
  ) {
    football {
      competition(slug: $slug) {
        slug
        playersByLastFiveAverage(first: 5) @include(if: $queryScore) {
          nodes {
            slug
            sport
            ...TopPlayers_player
            ...PlayerScore_anyPlayer
            ...getAppearancePercentage_anyPlayerInterface
          }
        }
        orderedPlayers(
          first: 5
          averageStat: $averageStatForOrderedPlayers
          limit: LAST_15
        ) @skip(if: $queryScore) {
          nodes {
            slug
            sport
            averageStats(type: $averageStat, limit: LAST_15)
            ...TopPlayers_player
          }
        }
      }
    }
  }
  ${playerFragment}
  ${getAppearancePercentage.fragments.anyPlayer}
  ${PlayerScore.fragments.anyPlayer}
` as TypedDocumentNode<TopPlayersLeagueQuery, TopPlayersLeagueQueryVariables>;

const StyledVertical = styled(Vertical)`
  align-items: end;
`;

const STATS_AVAILABLE = [
  'last5',
  AverageStat.GOALS,
  AverageStat.GOAL_ASSIST,
  AverageStat.CLEAN_SHEET,
  AverageStat.BIG_CHANCE_CREATED,
  AverageStat.WON_TACKLE,
  AverageStat.INTERCEPTION_WON,
] as const;
type StatsAvailable = (typeof STATS_AVAILABLE)[number];

const statMessages: Record<StatsAvailable, MessageDescriptor> = {
  last5: cardAttributes.averageScore,
  [AverageStat.GOALS]: attributes.goals,
  [AverageStat.GOAL_ASSIST]: attributes.assists,
  [AverageStat.CLEAN_SHEET]: messages.cleanSheets,
  [AverageStat.BIG_CHANCE_CREATED]: messages.bigChancesCreated,
  [AverageStat.WON_TACKLE]: messages.tacklesWon,
  [AverageStat.INTERCEPTION_WON]: messages.interceptionsWon,
};

export const TopPlayers = ({ competitionSlug }: Props) => {
  const { formatMessage } = useIntl();
  const [stat, setStat] = useState<StatsAvailable>('last5');
  const statIsScore = stat === 'last5';

  const { data, loading } = useQuery(TOP_PLAYERS_LEAGUE_QUERY, {
    variables: {
      slug: competitionSlug,
      averageStatForOrderedPlayers: statIsScore ? undefined : stat,
      averageStat: statIsScore ? AverageStat.GOALS : stat, // will be ignored if queryScore is true (if statIsScore is true)
      queryScore: statIsScore,
    },
  });

  if (loading) return <LoadingIndicator />;

  const stats = statIsScore ? ['l5', 'l15'] : [stat];
  const headers = (
    statIsScore
      ? {
          l5: FILTERS.lastFiveAverageScore.alias,
          l15: FILTERS.lastFifteenAverageScore.alias,
        }
      : {
          [stat]: (
            <StyledHorizontal as="span">
              <FormattedMessage {...statMessages[stat]} />
              <Tooltip
                title={
                  <FormattedMessage
                    id="League.Overview.TopPlayers.averageOverLast15Games"
                    defaultMessage="Average over Last 15 Games"
                  />
                }
              >
                <FontAwesomeIcon icon={faInfoCircle} />
              </Tooltip>
            </StyledHorizontal>
          ),
        }
  ) as Record<(typeof stats)[number], string>;

  const playersWithScoreStats =
    data?.football.competition.playersByLastFiveAverage?.nodes.map(player => ({
      ...player,
      stats: {
        l5: (
          <StyledVertical gap={0.5}>
            <PlayerScore
              player={player}
              mode={AveragePlayerScore.LAST_FIVE_SO5_AVERAGE_SCORE}
            />
            <LabelS as="p" color="var(--c-nd-600)">
              {getAppearancePercentage(
                AveragePlayerScore.LAST_FIVE_SO5_AVERAGE_SCORE,
                player
              )}
              %
            </LabelS>
          </StyledVertical>
        ),
        l15: (
          <StyledVertical gap={0.5}>
            <PlayerScore
              player={player}
              mode={AveragePlayerScore.LAST_FIFTEEN_SO5_AVERAGE_SCORE}
            />
            <LabelS as="p" color="var(--c-nd-600)">
              {getAppearancePercentage(
                AveragePlayerScore.LAST_FIFTEEN_SO5_AVERAGE_SCORE,
                player
              )}
              %
            </LabelS>
          </StyledVertical>
        ),
      },
      to: generateSportPath(ANY_SPORT_PLAYERS_SLUG, {
        params: { slug: player.slug },
        sport: player.sport,
      }),
    }));

  const playersWithAverageStat =
    data?.football.competition.orderedPlayers?.nodes.map(player => ({
      ...player,
      stats: {
        [stat]: player.averageStats !== null && (
          <div>
            <BodyM as="p" bold>
              <FormattedNumber
                value={player.averageStats}
                minimumFractionDigits={2}
                maximumFractionDigits={2}
              />
            </BodyM>
          </div>
        ),
      },
      to: generateSportPath(ANY_SPORT_PLAYERS_SLUG, {
        params: { slug: player.slug },
        sport: player.sport,
      }),
    }));

  const options = STATS_AVAILABLE.map(s => ({
    label: formatMessage(statMessages[s]),
    value: s,
  }));

  return (
    <FullWidthContainer>
      <Vertical gap={1.5}>
        <HeadlineS as="h2">
          <FormattedMessage {...glossary.topPlayers} />
        </HeadlineS>
        <div>
          <FilterInDropdown
            buttonLabel={<FormattedMessage {...statMessages[stat]} />}
          >
            {({ closeDropdown }) => (
              <RadioGroup
                modalSelect
                options={options}
                initiallySelectedValue={stat}
                name="club-standings-season"
                onChange={value => {
                  const selectedStat = STATS_AVAILABLE.find(
                    statAvailable => statAvailable === value
                  );
                  if (selectedStat) {
                    setStat(selectedStat);
                    closeDropdown();
                  }
                }}
              />
            )}
          </FilterInDropdown>
        </div>
        <Vertical>
          <Row
            style={{
              gridTemplateColumns: `auto 1fr repeat(${stats.length}, calc(${
                16 / stats.length
              } * var(--unit)))`,
            }}
          >
            <StyledLabelS as="p" color="var(--c-nd-600)" bold>
              <FormattedMessage {...glossary.name} />
            </StyledLabelS>
            <span />
            {stats.map(s => (
              <Header as="p" key={s} color="var(--c-nd-600)" bold>
                {headers[s]}
              </Header>
            ))}
          </Row>
          {(statIsScore ? playersWithScoreStats : playersWithAverageStat)?.map(
            player => (
              <TopPlayer key={player.slug} player={player} stats={stats} />
            )
          )}
        </Vertical>
      </Vertical>
    </FullWidthContainer>
  );
};
